import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import {
  Check, ChevronRight, Layers, Calendar, Users,
  MapPin, Crown, BookOpen, RefreshCw, Brain, Sparkles
} from "lucide-react";

import SEO from "../../components/SEO";

const FlashcardsPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    {
      icon: <Calendar className="w-6 h-6" />,
      name: "Important Dates",
      count: "50+",
      description: "Key dates in Canadian history including Confederation, World Wars, and constitutional milestones."
    },
    {
      icon: <Users className="w-6 h-6" />,
      name: "Key Personalities",
      count: "40+",
      description: "Prime Ministers, Fathers of Confederation, notable Canadians, and historical figures."
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      name: "Geography",
      count: "35+",
      description: "Provinces, territories, capitals, regions, and geographic features of Canada."
    },
    {
      icon: <Crown className="w-6 h-6" />,
      name: "Government",
      count: "45+",
      description: "Federal, provincial, and municipal government structures, elections, and institutions."
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      name: "Rights & Symbols",
      count: "30+",
      description: "Canadian symbols, national holidays, rights, and responsibilities of citizens."
    }
  ];

  const features = [
    {
      icon: <RefreshCw className="w-6 h-6" />,
      title: "Flip to Learn",
      description: "Interactive cards you flip to reveal answers—proven to improve memory retention."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Self-Assessment",
      description: "Mark cards as 'Got it' or 'Need review' to focus on what you need to learn."
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Category Filters",
      description: "Study by category or shuffle all cards for comprehensive review sessions."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Mobile Friendly",
      description: "Practice anywhere on your phone during commutes, lunch breaks, or spare moments."
    }
  ];

  return (
    <>
      <SEO
        title="Flashcards — 200+ Cards for Key Dates, People & Facts"
        description="Study 200+ Canadian citizenship test flashcards covering important dates in Canadian history, key personalities, and essential facts from Discover Canada. Interactive flip-card interface with mastery tracking. 10 free flashcards included."
        keywords="Canadian citizenship flashcards, Discover Canada flashcards, Canadian history dates, important dates Canada, key personalities Canada, citizenship study cards, Canadian civics flashcards"
        canonical="https://canadatestprep.ca/features/flashcards"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Features" },
          { name: "Flashcards", url: "/features/flashcards" }
        ]}
      />
      <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-purple-500/10 text-purple-600 mb-4">Flashcards</Badge>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-slate-900 leading-tight mb-6">
                Memorize Key Facts with{" "}
                <span className="text-purple-600">200+ Flashcards</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                The Canadian Citizenship Test often asks about specific dates, people, and facts. 
                Our flashcard system helps you memorize these critical details quickly and effectively 
                using proven spaced repetition techniques.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/register">
                  <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-8" data-testid="hero-cta">
                    Start Learning
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>200+ Flashcards</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>5 Categories</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Mobile Ready</span>
                </div>
              </div>
            </div>
            <div className="relative">
              {/* Flashcard Illustration */}
              <div className="relative w-full max-w-md mx-auto">
                {/* Stack effect */}
                <div className="absolute top-4 left-4 right-4 h-48 bg-purple-200 rounded-2xl transform rotate-3"></div>
                <div className="absolute top-2 left-2 right-2 h-48 bg-purple-300 rounded-2xl transform -rotate-2"></div>
                {/* Main card */}
                <Card className="relative bg-white border-2 border-purple-400 shadow-xl">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <Badge className="bg-purple-100 text-purple-600 mb-4">Important Dates</Badge>
                      <h3 className="text-xl font-semibold text-slate-900 mb-6">
                        When did Canada become a country through Confederation?
                      </h3>
                      <div className="border-t border-dashed border-slate-200 pt-6">
                        <p className="text-2xl font-bold text-purple-600">July 1, 1867</p>
                        <p className="text-sm text-slate-500 mt-2">
                          The British North America Act united Ontario, Quebec, Nova Scotia, and New Brunswick into the Dominion of Canada.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Flashcard Categories
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Our flashcards are organized into categories matching the most commonly tested topics on the citizenship exam.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <Card key={index} className="border border-slate-200 hover:border-purple-300 hover:shadow-md transition-all" data-testid={`category-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 flex-shrink-0">
                      {category.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900">{category.name}</h3>
                        <Badge variant="secondary" className="text-xs">{category.count}</Badge>
                      </div>
                      <p className="text-sm text-slate-600">{category.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              How Our Flashcards Help You Learn
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Interactive flashcards are proven to be more effective than passive reading. 
              Here's how our system maximizes your learning.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white border border-slate-200" data-testid={`feature-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-slate max-w-none">
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">
              Why Flashcards Are Essential for Canadian Citizenship Test Prep
            </h2>
            <p className="text-slate-600 mb-4">
              The Canadian Citizenship Test frequently asks about specific facts that require memorization: 
              dates, names, places, and definitions. While understanding concepts is important, 
              there's no substitute for knowing exact answers to questions like:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>When did Confederation occur? (July 1, 1867)</li>
              <li>Who was the first Prime Minister of Canada? (Sir John A. Macdonald)</li>
              <li>What are the three levels of government? (Federal, Provincial/Territorial, Municipal)</li>
              <li>Which province is the largest by area? (Quebec)</li>
              <li>When did women get the right to vote in federal elections? (1918)</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Our 200+ flashcards cover all these commonly tested facts, organized into easy-to-study categories.
            </p>

            <h3 className="font-heading font-semibold text-xl text-slate-900 mt-8 mb-4">
              Tips for Using Flashcards Effectively
            </h3>
            <ol className="list-decimal pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Study in short sessions:</strong> 15-20 minutes at a time works best for retention</li>
              <li><strong>Review daily:</strong> Consistent practice beats cramming before your test</li>
              <li><strong>Focus on weak areas:</strong> Use our "Need review" feature to prioritize difficult cards</li>
              <li><strong>Mix categories:</strong> Shuffle all cards occasionally for comprehensive review</li>
              <li><strong>Practice on mobile:</strong> Use spare moments during your day to reinforce learning</li>
            </ol>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-purple-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-6">
            Start Memorizing Key Facts Today
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Access all 200+ flashcards and master the facts you need to pass your citizenship test.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-purple-600 hover:bg-slate-100 rounded-full px-10" data-testid="cta-final">
              Get Started Free
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
    </>
  );
};

export default FlashcardsPage;


